import React from 'react';
import './Pagination.css';

export const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

/** Page numbers to show: first, last, and a window around the current page. */
const pageWindow = (page, pages) => {
  if (pages <= 7) return Array.from({ length: pages }, (_, i) => i + 1);
  const items = new Set([1, pages, page - 1, page, page + 1]);
  if (page <= 3) [2, 3, 4].forEach((p) => items.add(p));
  if (page >= pages - 2) [pages - 3, pages - 2, pages - 1].forEach((p) => items.add(p));
  const sorted = [...items].filter((p) => p >= 1 && p <= pages).sort((a, b) => a - b);
  const out = [];
  sorted.forEach((p, i) => {
    if (i > 0 && p - sorted[i - 1] > 1) out.push('…');
    out.push(p);
  });
  return out;
};

/**
 * Pagination bar: "Showing 1–25 of 132", page buttons, and a rows-per-page
 * selector. Works for both client-side paging (see usePagination) and
 * server-side paging (pass the values the API returns).
 *
 * Renders nothing when there is nothing to page.
 */
const Pagination = ({
  page = 1,
  pages = 1,
  total,
  pageSize,
  from,
  to,
  onPageChange,
  onPageSizeChange,
  loading = false,
  noun = 'items'
}) => {
  const hasTotal = typeof total === 'number';
  if ((!hasTotal || total === 0) && pages <= 1) return null;

  const start = from ?? (hasTotal ? Math.min(total, (page - 1) * pageSize + 1) : null);
  const end = to ?? (hasTotal ? Math.min(total, page * pageSize) : null);

  const go = (next) => {
    if (next < 1 || next > pages || next === page || loading) return;
    onPageChange(next);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="pagination-bar">
      <div className="pagination-summary">
        {hasTotal
          ? `Showing ${start}–${end} of ${total} ${noun}`
          : `Page ${page} of ${pages}`}
      </div>

      <div className="pagination-controls">
        <button type="button" className="pagination-page-btn" onClick={() => go(page - 1)} disabled={page <= 1 || loading}>
          ‹ Prev
        </button>
        {pageWindow(page, pages).map((item, index) =>
          item === '…' ? (
            <span key={`gap-${index}`} className="pagination-ellipsis">…</span>
          ) : (
            <button
              key={item}
              type="button"
              className={`pagination-page-btn ${item === page ? 'active' : ''}`}
              onClick={() => go(item)}
              disabled={loading}
              aria-current={item === page ? 'page' : undefined}
            >
              {item}
            </button>
          )
        )}
        <button type="button" className="pagination-page-btn" onClick={() => go(page + 1)} disabled={page >= pages || loading}>
          Next ›
        </button>
      </div>

      {onPageSizeChange && (
        <label className="pagination-size">
          Rows per page
          <select value={pageSize} onChange={(e) => onPageSizeChange(Number(e.target.value))} disabled={loading}>
            {PAGE_SIZE_OPTIONS.map((size) => (
              <option key={size} value={size}>{size}</option>
            ))}
          </select>
        </label>
      )}
    </div>
  );
};

export default Pagination;
